/**
 * @file Role toggle component for managing user roles
 * @version 1.0.0
 * @since 2025-08-25
 */

import { useUser } from '../composables/user.js';
import { useNotifications, useErrorHandler } from '../../core/errors.js';
import UserGroupRequestController from './UserGroupRequestController.js';

export default {
  name: 'UserRoleToggle',

  components: {
    UserGroupRequestController,
  },

  props: {
    showPendingRequests: {
      type: Boolean,
      default: true,
    },
    groups: {
      type: Array,
      default: null,
    },
    showRoleTags: {
      type: Boolean,
      default: false,
    },
  },

  template: `
    <div>
      <!-- Available Groups Section -->
      <div class="row" v-if="displayGroups.length > 0">
        <div 
          v-for="group in displayGroups" 
          :key="group.id"
          class="col-md-6 mb-3"
        >
          <div class="card h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1 me-2">
                  <h6 class="card-title mb-1">{{ group.name }}</h6>
                  <p class="text-muted small mb-2">{{ group.description }}</p>
                  <div v-if="showRoleTags" class="small">
                    <strong>Roles:</strong>
                    <span v-for="role in group.roles" :key="role" class="badge bg-secondary me-1 ms-1">
                      {{ role }}
                    </span>
                  </div>
                </div>
                <div class="flex-shrink-0">
                  <UserGroupRequestController :group-id="group.id" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fallback message when no groups available -->
      <div v-if="displayGroups.length === 0" class="alert alert-info">
        <i class="bi bi-info-circle"></i>
        No group request handler configured. Using direct role management.
      </div>
    </div>
  `,

  setup(props) {
    const user = useUser();

    // Computed property to determine which groups to display
    const displayGroups = Vue.computed(() => {
      if (props.groups === null) {
        return user.availableGroups;
      }
      return user.availableGroups.filter(group =>
        props.groups.includes(group.id)
      );
    });

    return {
      user,
      displayGroups,
    };
  },
};
